import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Castle, Coins, Loader2, Pencil, Shield, Swords, Trophy, Zap } from "lucide-react";
import { plyxApi as api } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { toast } from "sonner";
import { XpBar } from "@/pages/plyx/PlyxBrainClash";

const fmt = (n) => (n >= 10000 ? `${(n / 1000).toFixed(1)}k` : `${n}`);

const BuildingCard = ({ code, meta, level, cost, gold, maxLevel, onUpgrade, busy }) => (
  <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-4" data-testid={`kingdom-building-${code}`}>
    <div className="flex items-center justify-between">
      <span className="text-2xl">{meta.emoji}</span>
      <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-[#FFB42D]/15 text-[#FFB42D]">Lv {level}</span>
    </div>
    <div className="font-extrabold text-sm mt-1.5">{meta.label}</div>
    <div className="text-[11px] text-white/50 mb-2.5">{meta.desc}</div>
    {cost != null ? (
      <button type="button" onClick={() => onUpgrade(code)} disabled={busy || gold < cost}
        data-testid={`kingdom-upgrade-${code}`}
        className={`w-full py-2 rounded-full text-xs font-extrabold transition-colors ${gold >= cost ? "bg-[#FFB42D] text-black" : "bg-white/10 text-white/40"}`}>
        Upgrade · {fmt(cost)} 🪙
      </button>
    ) : (
      <div className="text-center text-xs font-bold text-[#00E0C7] py-2">MAX LEVEL</div>
    )}
  </div>
);

export default function PlyxKingdoms() {
  const { user } = usePlyxAuth();
  const navigate = useNavigate();
  const [k, setK] = useState(null);
  const [tab, setTab] = useState("kingdom");
  const [targets, setTargets] = useState(null);
  const [throne, setThrone] = useState(null);
  const [raids, setRaids] = useState([]);
  const [myKid, setMyKid] = useState(null);
  const [busy, setBusy] = useState(false);
  const [lastRaid, setLastRaid] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState("");

  const loadMe = useCallback(() => {
    if (!user) return;
    api.get("/plyx/kingdoms/me").then((r) => setK(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) { navigate("/plyx/login"); return; }
    loadMe();
  }, [user, loadMe, navigate]);

  useEffect(() => {
    if (!user) return;
    if (tab === "raid") {
      api.get("/plyx/kingdoms/targets").then((r) => setTargets(r.data.targets)).catch(() => {});
      api.get("/plyx/kingdoms/raids").then((r) => { setRaids(r.data.raids); setMyKid(r.data.my_kingdom_id); }).catch(() => {});
    }
    if (tab === "throne") api.get("/plyx/kingdoms/throne").then((r) => setThrone(r.data)).catch(() => {});
  }, [tab, user]);

  const upgrade = async (building) => {
    setBusy(true);
    try {
      const r = await api.post("/plyx/kingdoms/upgrade", { building });
      setK(r.data.kingdom);
      toast.success(`${r.data.kingdom.buildings_meta[building].label} upgraded! +10 XP`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upgrade failed");
    } finally { setBusy(false); }
  };

  const raid = async (kingdomId) => {
    setBusy(true);
    setLastRaid(null);
    try {
      const r = await api.post("/plyx/kingdoms/raid", { kingdom_id: kingdomId });
      setLastRaid(r.data);
      if (r.data.outcome === "win") toast.success(`Victory! You looted ${r.data.gold_stolen} gold (+${r.data.xp_awarded} XP)`);
      else toast.error("Your raid was repelled by their walls!");
      loadMe();
      api.get("/plyx/kingdoms/targets").then((rr) => setTargets(rr.data.targets)).catch(() => {});
      api.get("/plyx/kingdoms/raids").then((rr) => setRaids(rr.data.raids)).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Raid failed");
    } finally { setBusy(false); }
  };

  const rename = async () => {
    try {
      const r = await api.post("/plyx/kingdoms/rename", { name: newName });
      setK((p) => ({ ...p, name: r.data.name }));
      setRenaming(false);
      toast.success("Kingdom renamed!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    }
  };

  if (!k) {
    return (
      <div className="min-h-screen millions-stage text-white flex items-center justify-center" data-testid="kingdoms-loading">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Raising your kingdom…
      </div>
    );
  }

  return (
    <div className="min-h-screen millions-stage text-white pb-24" data-testid="plyx-kingdoms">
      <div className="max-w-2xl mx-auto px-4 pt-5">
        <Link to="/plyx/games" className="flex items-center gap-1 text-white/60 text-sm mb-3" data-testid="kingdoms-back-games">
          <ArrowLeft className="w-4 h-4" /> All games
        </Link>

        <div className="flex items-center gap-2 mb-1">
          <Castle className="w-7 h-7 text-[#a78bfa]" />
          {renaming ? (
            <div className="flex gap-2 flex-1">
              <input value={newName} onChange={(e) => setNewName(e.target.value)} maxLength={30}
                data-testid="kingdoms-rename-input"
                className="flex-1 min-w-0 px-3 py-1.5 rounded-full bg-white/[0.08] border border-white/20 text-sm outline-none" />
              <button type="button" onClick={rename} data-testid="kingdoms-rename-save"
                className="px-4 py-1.5 rounded-full bg-[#a78bfa] text-black text-xs font-extrabold">Save</button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-extrabold truncate" data-testid="kingdoms-name">{k.name}</h1>
              <button type="button" onClick={() => { setNewName(k.name); setRenaming(true); }} data-testid="kingdoms-rename-btn"
                className="text-white/40 hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
            </>
          )}
        </div>
        <p className="text-xs text-white/50 mb-4">Your gold grows with your Farm AND your cross-game XP level — every Plyx game you win makes this kingdom richer.</p>

        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="rounded-xl bg-[#FFB42D]/10 border border-[#FFB42D]/30 p-3 text-center" data-testid="kingdoms-gold">
            <Coins className="w-4 h-4 text-[#FFB42D] mx-auto mb-1" />
            <div className="font-extrabold">{fmt(k.gold)}</div>
            <div className="text-[10px] text-white/50">+{k.income_per_hour}/hr</div>
          </div>
          <div className="rounded-xl bg-[#a78bfa]/10 border border-[#a78bfa]/30 p-3 text-center" data-testid="kingdoms-power">
            <Zap className="w-4 h-4 text-[#a78bfa] mx-auto mb-1" />
            <div className="font-extrabold">{fmt(k.power)}</div>
            <div className="text-[10px] text-white/50">Power</div>
          </div>
          <div className="rounded-xl bg-white/[0.05] border border-white/10 p-3 text-center" data-testid="kingdoms-record">
            <Swords className="w-4 h-4 text-[#FF2D7A] mx-auto mb-1" />
            <div className="font-extrabold">{k.wins}W · {k.losses}L</div>
            <div className="text-[10px] text-white/50">{k.defends} defended</div>
          </div>
        </div>

        {k.shield_secs > 0 && (
          <div className="rounded-xl bg-[#00E0C7]/10 border border-[#00E0C7]/30 px-3 py-2 mb-4 text-xs font-bold flex items-center gap-2" data-testid="kingdoms-shield">
            <Shield className="w-4 h-4 text-[#00E0C7]" /> Shield active — nobody can raid you for {Math.ceil(k.shield_secs / 60)}m
          </div>
        )}

        <div className="flex gap-2 mb-4">
          {[["kingdom", "🏰 Kingdom"], ["raid", "⚔️ Raid"], ["throne", "👑 Throne"]].map(([code, label]) => (
            <button key={code} type="button" onClick={() => setTab(code)} data-testid={`kingdoms-tab-${code}`}
              className={`px-4 py-2 rounded-full text-sm font-extrabold ${tab === code ? "bg-[#a78bfa] text-black" : "bg-white/10"}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "kingdom" && (
          <div data-testid="kingdoms-buildings">
            <XpBar />
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(k.buildings_meta).map(([code, meta]) => (
                <BuildingCard key={code} code={code} meta={meta} level={k.buildings[code]}
                  cost={k.upgrade_costs[code]} gold={k.gold} maxLevel={k.max_level}
                  onUpgrade={upgrade} busy={busy} />
              ))}
            </div>
            <div className="text-[11px] text-white/40 mt-3">
              🏦 Vault protects {fmt(k.protected_gold)} gold from raiders · ⚔️ Attack {k.attack_power} · 🛡️ Defence {k.defence_power}
            </div>
          </div>
        )}

        {tab === "raid" && (
          <div data-testid="kingdoms-raid-tab">
            {k.raid_cooldown_secs > 0 && (
              <div className="rounded-xl bg-white/[0.05] border border-white/10 px-3 py-2 mb-3 text-xs font-bold" data-testid="kingdoms-cooldown">
                ⏳ Army resting — next raid in {Math.floor(k.raid_cooldown_secs / 60)}m {k.raid_cooldown_secs % 60}s
              </div>
            )}
            {lastRaid && (
              <div className={`rounded-2xl border p-4 mb-4 ${lastRaid.outcome === "win" ? "bg-[#00E0C7]/10 border-[#00E0C7]/40" : "bg-[#FF2D7A]/10 border-[#FF2D7A]/40"}`} data-testid="kingdoms-raid-result">
                <div className="font-extrabold mb-1">{lastRaid.outcome === "win" ? "🏆 Victory!" : "💥 Repelled!"}</div>
                <div className="text-xs text-white/70">
                  Your attack rolled <b>{lastRaid.attack_roll}</b> vs their defence <b>{lastRaid.defence_roll}</b>.
                  {lastRaid.outcome === "win" ? ` You looted ${lastRaid.gold_stolen} gold and earned +${lastRaid.xp_awarded} XP.` : " Train your Barracks and try again."}
                </div>
              </div>
            )}
            <div className="text-sm font-bold mb-2">Kingdoms you can raid</div>
            {!targets ? (
              <div className="text-sm text-white/40 py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Scouting…</div>
            ) : (
              <div className="space-y-2 mb-5" data-testid="kingdoms-targets">
                {targets.map((t) => (
                  <div key={t.kingdom_id} className="flex items-center gap-3 rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2.5" data-testid={`kingdoms-target-${t.kingdom_id}`}>
                    <span className="text-lg">{t.is_npc ? "🤖" : "🏰"}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{t.name}{t.owner_handle ? <span className="text-white/40 font-normal"> · @{t.owner_handle}</span> : ""}</div>
                      <div className="text-[11px] text-white/50">Power {fmt(t.power)} · 🛡️ Lv{t.walls} walls · loot up to {fmt(Math.floor(t.loot_potential * 0.25))} 🪙</div>
                    </div>
                    <button type="button" onClick={() => raid(t.kingdom_id)} disabled={busy || k.raid_cooldown_secs > 0}
                      data-testid={`kingdoms-raid-btn-${t.kingdom_id}`}
                      className="px-4 py-2 rounded-full bg-[#FF2D7A] text-white text-xs font-extrabold disabled:opacity-40">
                      Raid
                    </button>
                  </div>
                ))}
                {targets.length === 0 && <div className="text-sm text-white/40 py-4 text-center">All rival kingdoms are shielded — check back soon.</div>}
              </div>
            )}
            {raids.length > 0 && (
              <div data-testid="kingdoms-raid-log">
                <div className="text-xs font-bold text-white/50 mb-2">BATTLE LOG</div>
                <div className="space-y-1.5">
                  {raids.map((r) => {
                    const iAttacked = r.attacker_id === myKid;
                    const won = (r.outcome === "win") === iAttacked;
                    return (
                      <div key={r.raid_id} className="flex items-center gap-2 text-xs rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2">
                        <span>{iAttacked ? "⚔️" : "🛡️"}</span>
                        <span className="flex-1 text-white/70">
                          {iAttacked ? `You raided ${r.defender_name}` : `${r.attacker_name} raided you`}
                        </span>
                        <span className={`font-extrabold ${won ? "text-[#00E0C7]" : "text-[#FF2D7A]"}`}>
                          {r.outcome === "win" ? (iAttacked ? `+${r.gold_stolen} 🪙` : `-${r.gold_stolen} 🪙`) : "repelled"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "throne" && (
          <div data-testid="kingdoms-throne">
            {throne?.my_rank && (
              <div className="rounded-xl bg-[#a78bfa]/10 border border-[#a78bfa]/30 px-3 py-2 mb-3 text-sm font-bold" data-testid="kingdoms-my-rank">
                👑 Your throne rank: #{throne.my_rank}
              </div>
            )}
            {!throne ? (
              <div className="text-sm text-white/40 py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Summoning the court…</div>
            ) : (
              <div className="space-y-1.5">
                {throne.throne.map((t, i) => (
                  <div key={t.kingdom_id} className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border ${i === 0 ? "bg-[#FFB42D]/10 border-[#FFB42D]/40" : "bg-white/[0.04] border-white/10"}`}
                    data-testid={`kingdoms-throne-${i + 1}`}>
                    <span className="w-6 text-center font-extrabold text-[#FFB42D]">{i === 0 ? <Trophy className="w-4 h-4 inline" /> : i + 1}</span>
                    <span className="text-lg">{t.is_npc ? "🤖" : "🏰"}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{t.name}</div>
                      {t.owner_handle && <div className="text-[10px] text-white/40">@{t.owner_handle}</div>}
                    </div>
                    <span className="font-extrabold text-sm">{fmt(t.power)} ⚡</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
